import React, { ReactNode, useState, useCallback, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import logoImage from '../assets/logo.png';
import {
  Home,
  Heart,
  MessageCircle,
  User,
  Bell,
  Calendar,
  Globe
} from 'lucide-react';
import { useSSEEvent, ChatMessageEvent } from '../hooks/useEventStream';
import { chatAPI } from '../services/api';

interface LayoutProps {
  children: ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, seller, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const langDropdownRef = useRef<HTMLDivElement>(null);
  const [chatToast, setChatToast] = useState<{ sender: string; message: string } | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsProfileDropdownOpen(false);
      }
      if (langDropdownRef.current && !langDropdownRef.current.contains(event.target as Node)) {
        setIsLangDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  const [unreadCount, setUnreadCount] = useState(0);
  const isAuthenticated = Boolean(user || seller);

  // 초기 읽지 않은 메시지 수 가져오기 (경량 엔드포인트, 채팅 페이지에서는 건너뜀)
  useEffect(() => {
    if (!isAuthenticated) return;
    if (location.pathname === '/chat') return; // chat 페이지는 대화 목록에서 직접 계산
    chatAPI.getUnreadCount()
      .then(res => {
        setUnreadCount(res.data?.unread_count ?? 0);
      })
      .catch(() => {});
  }, [isAuthenticated, location.pathname]);

  // SSE 메시지 수신 시 글로벌 토스트 알림 + 읽지않음 카운트 증가
  useSSEEvent<ChatMessageEvent>('chat_message', useCallback((data: ChatMessageEvent) => {
    const senderName = data.message.sender?.nickname || '새 메시지';
    setChatToast({ sender: senderName, message: data.message.content });
    setUnreadCount(prev => prev + 1);
    setTimeout(() => setChatToast(null), 4000);
  }, []), []);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const handleLogout = () => {
    logout();
    navigate('/', { replace: true });
  };

  const handleProtectedClick = (e: React.MouseEvent) => {
    if (!isAuthenticated) {
      e.preventDefault();
      if (window.confirm('로그인이 필요한 서비스입니다. 지금 로그인하시겠습니까?')) {
        navigate('/login');
      }
    }
  };

  const COMPANY_NAME = '(주)진상마켓';
  const BUSINESS_NUMBER = '552-87-03682';
  const COMPANY_ADDRESS =
    '경기도 하남시 미사강변대로54번길 75, 1층 103호(풍산동,홍우프라자)';
  const COMPANY_PHONE = '02-427-0312';
  const REPRESENTATIVE = '이현미';
  const PRIVACY_EMAIL = 'jenerity04@gmail.com';

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col overflow-x-hidden max-w-full">
      {/* 글로벌 채팅 알림 토스트 */}
      {chatToast && (
        <div
          className="fixed top-20 left-1/2 -translate-x-1/2 z-[100] cursor-pointer"
          onClick={() => { setChatToast(null); navigate('/chat'); }}
        >
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 pl-4 pr-5 py-3 flex items-center gap-3 min-w-[280px] max-w-sm"
            style={{ animation: 'slideDown 0.3s ease-out, fadeOut 0.5s ease-in 3.5s forwards' }}
          >
            <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
              <MessageCircle size={20} className="text-primary-500" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-gray-900 truncate">{chatToast.sender}</p>
              <p className="text-xs text-gray-500 truncate">{chatToast.message}</p>
            </div>
          </div>
        </div>
      )}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to={seller ? "/seller-dashboard" : "/"} className="flex items-center">
              <img
                src={logoImage}
                alt="진상마켓"
                className="h-12 w-auto"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                  if (nextElement) {
                    nextElement.style.display = 'flex';
                  }
                }}
              />
              <div className="flex items-center space-x-3" style={{ display: 'none' }}>
                <div className="relative">
                  <div className="w-10 h-10 relative">
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-8 h-6 bg-blue-800 rounded-t-full"></div>
                    <div className="absolute top-4 left-0 w-10 h-2 bg-blue-800 rounded-full"></div>
                    <div className="absolute top-2 right-0 w-1 h-8 bg-sky-400 transform rotate-12 animate-pulse"></div>
                  </div>
                </div>
                <div className="flex flex-col">
                  <span className="text-xl font-bold text-gray-900" style={{ fontFamily: 'cursive' }}>진상마켓</span>
                  <span className="text-xs text-gray-600 -mt-1">진열상품 사리가자</span>
                </div>
              </div>
            </Link>

            <nav className="hidden md:flex items-center space-x-8">
              {!seller && (
                <>
                  <Link
                    to="/"
                    className={`flex items-center space-x-1 px-3 py-2 rounded-lg transition-colors ${isActive('/') ? 'text-primary-500 bg-primary-50' : 'text-gray-600 hover:text-primary-500'
                      }`}
                  >
                    <Home size={20} />
                    <span>{t('header.home')}</span>
                  </Link>
                  <Link
                    to="/reservations"
                    onClick={handleProtectedClick}
                    className={`flex items-center space-x-1 px-3 py-2 rounded-lg transition-colors ${isActive('/reservations') ? 'text-primary-500 bg-primary-50' : 'text-gray-600 hover:text-primary-500'
                      }`}
                  >
                    <Calendar size={20} />
                    <span>{t('header.reservations')}</span>
                  </Link>
                  <Link
                    to="/wishlist"
                    onClick={handleProtectedClick}
                    className={`flex items-center space-x-1 px-3 py-2 rounded-lg transition-colors ${isActive('/wishlist') ? 'text-primary-500 bg-primary-50' : 'text-gray-600 hover:text-primary-500'
                      }`}
                  >
                    <Heart size={20} />
                    <span>{t('header.wishlist')}</span>
                  </Link>
                  <Link
                    to="/chat"
                    onClick={handleProtectedClick}
                    className={`flex items-center space-x-1 px-3 py-2 rounded-lg transition-colors ${isActive('/chat') ? 'text-primary-500 bg-primary-50' : 'text-gray-600 hover:text-primary-500'
                      }`}
                  >
                    <MessageCircle size={20} />
                    <span>{t('header.chat')}</span>
                    {unreadCount > 0 && (
                      <span className="min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </Link>
                </>
              )}

              {!seller && (
                <Link
                  to="/alerts"
                  onClick={handleProtectedClick}
                  className={`flex items-center space-x-1 px-3 py-2 rounded-lg transition-colors ${isActive('/alerts') ? 'text-primary-500 bg-primary-50' : 'text-gray-600 hover:text-primary-500'
                    }`}
                >
                  <Bell size={20} />
                  <span>{t('header.keywordAlert')}</span>
                </Link>
              )}
            </nav>

            <div className="flex items-center space-x-3 md:space-x-4">
              {/* 언어 선택기 */}
              <div className="relative" ref={langDropdownRef}>
                <button
                  onClick={() => setIsLangDropdownOpen(!isLangDropdownOpen)}
                  className="p-2 text-gray-500 hover:text-primary-500 hover:bg-primary-50 rounded-full transition-colors focus:outline-none"
                  aria-label="Language"
                >
                  <Globe size={20} />
                </button>
                {isLangDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-32 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50">
                    {[
                      { code: 'ko', label: '한국어', flagUrl: 'https://flagcdn.com/w20/kr.png' },
                      { code: 'en', label: 'English', flagUrl: 'https://flagcdn.com/w20/us.png' },
                      { code: 'zh', label: '中文', flagUrl: 'https://flagcdn.com/w20/cn.png' },
                      { code: 'ja', label: '日本語', flagUrl: 'https://flagcdn.com/w20/jp.png' },
                      { code: 'tl', label: 'Tagalog', flagUrl: 'https://flagcdn.com/w20/ph.png' }
                    ].map(lang => (
                      <button
                        key={lang.code}
                        onClick={() => {
                          i18n.changeLanguage(lang.code);
                          setIsLangDropdownOpen(false);
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors ${
                          i18n.language.startsWith(lang.code) 
                            ? 'bg-primary-50 text-primary-600 font-medium' 
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <img src={lang.flagUrl} alt={lang.code} className="w-5 h-auto rounded-sm shadow-sm" />
                        <span>{lang.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {isAuthenticated ? (
                <div className="flex items-center space-x-4">
                  {(user || seller) && (
                    <div className="relative" ref={dropdownRef}>
                      <button
                        onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                        className="flex items-center space-x-2 text-gray-700 hover:text-primary-500 transition-colors focus:outline-none"
                      >
                        <div className="relative w-8 h-8 flex-shrink-0">
                          {(() => {
                            if (seller) {
                              if (seller.images && seller.images.length > 0) {
                                const primaryImage = seller.images.find(img => img.is_primary) || seller.images[0];
                                if (primaryImage?.image) {
                                  return (
                                    <>
                                      <img
                                        src={primaryImage.image}
                                        alt={seller.business_name || '매장'}
                                        className="w-8 h-8 rounded-full object-cover border-2 border-gray-200"
                                        onError={(e) => {
                                          e.currentTarget.style.display = 'none';
                                          const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                                          if (fallback) fallback.style.display = 'flex';
                                        }}
                                      />
                                      <div className="w-8 h-8 bg-gray-300 rounded-full items-center justify-center hidden" style={{ display: 'none' }}><User size={16} /></div>
                                    </>
                                  );
                                }
                              }
                              return (<div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center"><User size={16} className="text-gray-600" /></div>);
                            }
                            if (user?.profile_image) {
                              return (
                                <>
                                  <img
                                    src={user.profile_image}
                                    alt={user.nickname || '프로필'}
                                    className="w-8 h-8 rounded-full object-cover border-2 border-gray-200"
                                    onError={(e) => {
                                      e.currentTarget.style.display = 'none';
                                      const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                                      if (fallback) fallback.style.display = 'flex';
                                    }}
                                  />
                                  <div className="w-8 h-8 bg-gray-300 rounded-full items-center justify-center hidden" style={{ display: 'none' }}><User size={16} /></div>
                                </>
                              );
                            }
                            return (<div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center"><User size={16} className="text-gray-600" /></div>);
                          })()}
                        </div>
                        <span className="hidden sm:block">
                          {user?.nickname || seller?.business_name}
                        </span>
                      </button>

                      {/* 프로필 드롭다운 메뉴 */}
                      {isProfileDropdownOpen && (
                        <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50">
                          <Link
                            to="/profile"
                            onClick={() => setIsProfileDropdownOpen(false)}
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-600 transition-colors"
                          >
                            {t('header.profile')}
                          </Link>
                          <button
                            onClick={() => {
                              setIsProfileDropdownOpen(false);
                              handleLogout();
                            }}
                            className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                          >
                            {t('header.logout')}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <Link
                    to="/login"
                    className="text-sm font-medium text-gray-600 hover:text-primary-500 transition-colors px-3 py-1.5 rounded-lg border border-gray-200 hover:border-primary-200 hover:bg-primary-50"
                  >
                    {t('header.login')}
                  </Link>
                </div>
              )}

            </div>
          </div>
        </div>
      </header>
      <main className={
        location.pathname === '/chat'
          ? 'w-full px-4 sm:px-6 lg:px-8 py-3 flex-1 overflow-hidden'
          : 'max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-20 md:pb-8 flex-1 overflow-hidden'
      }>{children}</main>

      {location.pathname !== '/chat' && (
      <footer className="bg-gray-900 text-gray-200 border-t border-gray-800 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <div className="text-sm text-gray-400 mb-3">{t('footer.companyInfo')}</div>
                <div className="space-y-2 text-sm leading-relaxed">
                <div className="font-semibold text-gray-100">{COMPANY_NAME}</div>
                <div>{t('footer.ceo')}: {REPRESENTATIVE}</div>
                  <div>{t('footer.businessNumber')}: {BUSINESS_NUMBER}</div>
                  <div>{t('footer.address')}: {COMPANY_ADDRESS}</div>
                  <div>{t('footer.phone')}: {COMPANY_PHONE}</div>
                  <div className="text-gray-300">{t('footer.email')}: {PRIVACY_EMAIL}</div>
              </div>
            </div>

            <div>
              <div className="text-sm text-gray-400 mb-3">{t('footer.termsAndPolicies')}</div>
              <div className="flex flex-wrap gap-4">
                <Link to="/terms" className="text-sm text-primary-400 hover:underline">
                  {t('footer.terms')}
                </Link>
                <Link to="/privacy" className="text-sm text-primary-400 hover:underline">
                  {t('footer.privacy')}
                </Link>
                <Link to="/location" className="text-sm text-primary-400 hover:underline">
                  {t('footer.locationTerms')}
                </Link>
                <Link to="/youth" className="text-sm text-primary-400 hover:underline">
                  {t('footer.youthPolicy')}
                </Link>
              </div>
              <div className="mt-6 text-xs text-gray-500">
                &copy; {new Date().getFullYear()} {COMPANY_NAME}. All rights reserved.
              </div>
            </div>
          </div>
        </div>
      </footer>
      )}

      {/* 모바일 하단 네비게이션 */}
      {!seller && (
        <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50 safe-area-bottom">
          <div className="flex items-center justify-around py-2 px-1">
            <Link
              to="/"
              className={`flex flex-col items-center gap-0.5 px-2 py-1 rounded-lg transition-colors min-w-[56px] ${isActive('/') ? 'text-primary-500' : 'text-gray-400'}`}
            >
              <Home size={22} />
              <span className="text-[10px] font-medium">{t('header.home')}</span>
            </Link>
            <Link
              to="/reservations"
              onClick={handleProtectedClick}
              className={`flex flex-col items-center gap-0.5 px-2 py-1 rounded-lg transition-colors min-w-[56px] ${isActive('/reservations') ? 'text-primary-500' : 'text-gray-400'}`}
            >
              <Calendar size={22} />
              <span className="text-[10px] font-medium">{t('header.reservations')}</span>
            </Link>
            <Link
              to="/wishlist"
              onClick={handleProtectedClick}
              className={`flex flex-col items-center gap-0.5 px-2 py-1 rounded-lg transition-colors min-w-[56px] ${isActive('/wishlist') ? 'text-primary-500' : 'text-gray-400'}`}
            >
              <Heart size={22} />
              <span className="text-[10px] font-medium">{t('header.wishlist')}</span>
            </Link>
            <Link
              to="/chat"
              onClick={handleProtectedClick}
              className={`flex flex-col items-center gap-0.5 px-2 py-1 rounded-lg transition-colors min-w-[56px] relative ${isActive('/chat') ? 'text-primary-500' : 'text-gray-400'}`}
            >
              <div className="relative">
                <MessageCircle size={22} />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-2 min-w-[16px] h-[16px] flex items-center justify-center bg-red-500 text-white text-[9px] font-bold rounded-full px-1">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </div>
              <span className="text-[10px] font-medium">{t('header.chat')}</span>
            </Link>
            <Link
              to="/alerts"
              onClick={handleProtectedClick}
              className={`flex flex-col items-center gap-0.5 px-2 py-1 rounded-lg transition-colors min-w-[56px] ${isActive('/alerts') ? 'text-primary-500' : 'text-gray-400'}`}
            >
              <Bell size={22} />
              <span className="text-[10px] font-medium">{t('header.keywordAlert')}</span>
            </Link>
          </div>
        </nav>
      )}
    </div>
  );
};

export default Layout;
